import React from "react";
import { Plus, Check, X } from "lucide-react";
import { fmt } from "@/lib/calculations";

export const LineItemForm = ({
  form,
  updateForm,
  addLineItem,
  editingId,
  setEditingId,
  setForm,
  setTouched,
  EMPTY_FORM,
  STATES,
  fieldInvalid,
  isAdp,
  adpUnbundled,
  wcAutoFill,
  sutaAutoFill,
  duplicateWarning,
  sutaBases,
  customSutaBases,
}) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="add-line-item-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold font-['Outfit'] text-slate-900">
          {editingId ? "Edit Line Item" : "Add Line Item"}
        </h2>
        {editingId && (
          <button 
            data-testid="cancel-edit-btn" 
            onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setTouched({}); }}
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <X size={14} /> Cancel Edit
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">State</label>
          <select 
            data-testid="state-select" 
            value={form.state} 
            onChange={e => updateForm("state", e.target.value)}
            className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
          >
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        
        <div>
          <label className={`block text-xs font-medium uppercase tracking-wider mb-1.5 ${fieldInvalid("wcCode") ? "text-red-500" : "text-slate-500"}`}>
            WC Class Code {fieldInvalid("wcCode") && <span className="text-red-500">*</span>}
          </label>
          <input 
            data-testid="wc-code-input" 
            value={form.wcCode} 
            onChange={e => updateForm("wcCode", e.target.value)}
            placeholder="e.g. 8810" 
            onKeyDown={e => e.key === "Enter" && addLineItem()}
            className={`w-full h-10 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1 ${fieldInvalid("wcCode") ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} 
          />
        </div>
        
        <div>
          <label className={`block text-xs font-medium uppercase tracking-wider mb-1.5 ${fieldInvalid("employeeCount") ? "text-red-500" : "text-slate-500"}`}>
            EE Count {fieldInvalid("employeeCount") && <span className="text-red-500">*</span>}
          </label>
          <input 
            data-testid="employee-count-input" 
            type="number" 
            min="1" 
            value={form.employeeCount} 
            onChange={e => updateForm("employeeCount", e.target.value)}
            placeholder="e.g. 7" 
            onKeyDown={e => e.key === "Enter" && addLineItem()}
            className={`w-full h-10 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1 ${fieldInvalid("employeeCount") ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} 
          />
        </div>
        
        <div>
          <label className={`block text-xs font-medium uppercase tracking-wider mb-1.5 ${fieldInvalid("grossWages") ? "text-red-500" : "text-slate-500"}`}>
            Gross Annual Wages {fieldInvalid("grossWages") && <span className="text-red-500">*</span>}
          </label>
          <input 
            data-testid="gross-wages-input" 
            type="number" 
            min="0" 
            step="1000" 
            value={form.grossWages} 
            onChange={e => updateForm("grossWages", e.target.value)}
            placeholder="e.g. 350000" 
            onKeyDown={e => e.key === "Enter" && addLineItem()}
            onBlur={e => { 
              const v = parseFloat(e.target.value); 
              if (!isNaN(v) && v > 0 && v < 1000) { 
                updateForm("grossWages", String(v * 1000)); 
              }
            }}
            className={`w-full h-10 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1 ${fieldInvalid("grossWages") ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} 
          />
        </div>
        
        <div>
          <label className={`block text-xs font-medium uppercase tracking-wider mb-1.5 ${fieldInvalid("wcRate") ? "text-red-500" : "text-slate-500"}`}>
            {isAdp && !adpUnbundled ? "ADP Service Fee %" : "WC Rate %"} {fieldInvalid("wcRate") && <span className="text-red-500">*</span>}
          </label>
          <input 
            data-testid="wc-rate-input" 
            type="number" 
            step="0.01" 
            value={form.wcRate} 
            onChange={e => updateForm("wcRate", e.target.value)}
            placeholder="e.g. 0.35" 
            onKeyDown={e => e.key === "Enter" && addLineItem()}
            className={`w-full h-10 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1 ${fieldInvalid("wcRate") ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} 
          />
        </div>
        
        <div>
          <label className={`block text-xs font-medium uppercase tracking-wider mb-1.5 ${fieldInvalid("sutaRate") ? "text-red-500" : "text-slate-500"}`}>
            SUTA Rate % {fieldInvalid("sutaRate") && <span className="text-red-500">*</span>}
          </label>
          <input 
            data-testid="suta-rate-input" 
            type="number" 
            step="0.01" 
            value={form.sutaRate} 
            onChange={e => updateForm("sutaRate", e.target.value)}
            placeholder="e.g. 3.0" 
            onKeyDown={e => e.key === "Enter" && addLineItem()}
            className={`w-full h-10 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1 ${fieldInvalid("sutaRate") ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} 
          />
        </div>
      </div>

      {/* Auto-fill messages */}
      <div className="mt-3 flex flex-wrap gap-4 items-center">
        {wcAutoFill && (
          <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded" data-testid="wc-autofill-msg">
            {wcAutoFill}
          </span>
        )}
        {sutaAutoFill && (
          <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded" data-testid="suta-autofill-msg">
            {sutaAutoFill}
          </span>
        )}
        {duplicateWarning && (
          <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded" data-testid="duplicate-warning">
            {duplicateWarning}
          </span>
        )}
        {form.state && sutaBases[form.state] && (
          <span className="text-xs text-slate-500">
            {form.state} SUTA Wage Base: <strong className="text-slate-800 font-mono">{fmt(sutaBases[form.state])}</strong>
            {customSutaBases && <span className="text-emerald-600 ml-1">(custom)</span>}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          data-testid="add-line-item-btn"
          onClick={addLineItem}
          className="flex items-center gap-1.5 px-5 py-2.5 bg-[#3c68ff] text-white rounded-lg text-sm font-medium hover:bg-[#2b4bca] transition-all shadow-sm"
        >
          {editingId ? <Check size={15} /> : <Plus size={15} />}
          {editingId ? "Update Item" : "Add to Quote"}
        </button>
        <span className="text-xs text-slate-400">Press Enter to add</span>
      </div>
    </div>
  );
};
